import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, ListChecks, PiggyBank, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { SelectItem } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { FormField } from '../../components/shared/FormField';
import UserSelect from '../../components/shared/UserSelect';
import { User } from '../../services/userService';
import { SelectField } from '../../components/shared/SelectField';
import { StatCard } from '../../components/shared/StatCard';
import ActionDropdown, { ActionItem } from '../../components/Common/ActionDropdown';
import ConfirmModal from '../../components/Common/ConfirmModal';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatCount } from '../../lib/formatNumber';
import cloudAssetService, {
  CloudRecommendation,
  CloudRecommendationState,
  CloudResource,
  RECOMMENDATION_STATE_LABELS,
  RECOMMENDATION_TRANSITIONS,
} from '../../services/cloudAssetService';


interface Props {
  resources: CloudResource[];
  /** Full user records — `UserSelect` searches and displays email too. */
  users: User[];
  currencies: string[];
}

const EMPTY = {
  title: '',
  description: '',
  category: '',
  estimatedMonthlySaving: '',
  currency: 'INR',
  assignedToId: '',
  dueDate: '',
  resourceIds: [] as number[],
};

/** Radix Select has no empty option value, so this sentinel carries "unset". */
const ALL_STATES = '__all__';

const STATE_VARIANT: Record<CloudRecommendationState, string> = {
  open: 'default',
  acknowledged: 'secondary',
  in_progress: 'secondary',
  actioned: 'outline',
  dismissed: 'outline',
};

/**
 * Cloud recommendations (User Story 89).
 *
 * Nothing here is detected automatically — a manual register has no
 * utilization data to detect from. The value is the follow-through: an owner,
 * a due date, and a decision that gets recorded either way.
 */
export const CloudRecommendationsTab: React.FC<Props> = ({
  resources,
  users,
  currencies,
}) => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatCost } = useCurrency();
  const canManage = hasPermission('cloud.recommendation.manage');

  const [recommendations, setRecommendations] = useState<CloudRecommendation[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<Record<string, any>>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [dismissing, setDismissing] = useState<CloudRecommendation | null>(null);
  const [dismissalReason, setDismissalReason] = useState('');
  const [pendingDelete, setPendingDelete] = useState<CloudRecommendation | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, counts] = await Promise.allSettled([
      cloudAssetService.getRecommendations(stateFilter || undefined),
      cloudAssetService.getRecommendationSummary(),
    ]);
    if (list.status === 'fulfilled') setRecommendations(list.value);
    if (counts.status === 'fulfilled') setSummary(counts.value);
    if (list.status === 'rejected') {
      showToast(
        (list.reason as any)?.friendlyMessage ||
          'Failed to load recommendations',
        'error',
      );
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!String(form.title).trim()) next.title = 'Title is required';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      await cloudAssetService.createRecommendation({
        title: form.title,
        description: form.description || undefined,
        category: form.category || undefined,
        estimatedMonthlySaving:
          form.estimatedMonthlySaving === ''
            ? undefined
            : Number(form.estimatedMonthlySaving),
        currency: form.currency,
        assignedToId:
          form.assignedToId === '' ? undefined : Number(form.assignedToId),
        dueDate: form.dueDate || undefined,
        resourceIds: form.resourceIds,
      });
      showToast('Recommendation raised', 'success');
      setDialogOpen(false);
      setForm(EMPTY);
      load();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to raise recommendation', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTransition = async (
    recommendation: CloudRecommendation,
    state: CloudRecommendationState,
  ) => {
    // Dismissal needs a reason, so it goes through its own prompt rather than
    // firing straight off the button.
    if (state === 'dismissed') {
      setDismissing(recommendation);
      setDismissalReason('');
      return;
    }
    try {
      await cloudAssetService.transitionRecommendation(recommendation.id, state);
      showToast(`Moved to ${RECOMMENDATION_STATE_LABELS[state]}`, 'success');
      load();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Could not change state', 'error');
    }
  };

  const confirmDismissal = async () => {
    if (!dismissing) return;
    if (!dismissalReason.trim()) {
      showToast('A reason is required to dismiss a recommendation', 'error');
      return;
    }
    try {
      await cloudAssetService.transitionRecommendation(
        dismissing.id,
        'dismissed',
        dismissalReason,
      );
      showToast('Recommendation dismissed', 'success');
      setDismissing(null);
      load();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Could not dismiss', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const recommendation = pendingDelete;
    setPendingDelete(null);
    try {
      await cloudAssetService.deleteRecommendation(recommendation.id);
      showToast('Recommendation deleted', 'success');
      load();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to delete', 'error');
    }
  };

  const toggleResource = (id: number) =>
    setForm((prev) => ({
      ...prev,
      resourceIds: prev.resourceIds.includes(id)
        ? prev.resourceIds.filter((value: number) => value !== id)
        : [...prev.resourceIds, id],
    }));

  return (
    <div>
      <div className="flex flex-col gap-2 border-b p-4 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="min-w-0 md:w-64">
          <SelectField
            value={stateFilter || ALL_STATES}
            onValueChange={(value) =>
              setStateFilter(value === ALL_STATES ? '' : value)
            }
            placeholder="All states"
          >
            <SelectItem value={ALL_STATES}>All states</SelectItem>
            {Object.entries(RECOMMENDATION_STATE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectField>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setDialogOpen(true)} className="md:shrink-0">
            <Plus className="mr-2 h-4 w-4" />
            Raise Recommendation
          </Button>
        )}
      </div>

      <div className="grid gap-4 p-4 sm:grid-cols-3 sm:p-6">
        <StatCard
          title="Open Work"
          value={formatCount(
            (summary.open ?? 0) +
              (summary.acknowledged ?? 0) +
              (summary.in_progress ?? 0),
          )}
          subtitle="raised but not yet settled"
          icon={ListChecks}
          iconColor="bg-indigo-500/10 text-indigo-500"
          loading={loading}
        />
        <StatCard
          title="Estimated Monthly Saving"
          value={formatCost(summary.estimatedMonthlySaving ?? 0)}
          subtitle="sum of human estimates on unsettled items"
          icon={PiggyBank}
          iconColor="bg-emerald-500/10 text-emerald-500"
          loading={loading}
        />
        <StatCard
          title="Actioned"
          value={formatCount(summary.actioned ?? 0)}
          subtitle="carried through to a change"
          icon={CheckCircle2}
          iconColor="bg-sky-500/10 text-sky-500"
          loading={loading}
        />
      </div>

      {loading ? (
        <p className="px-4 pb-6 text-sm text-muted-foreground sm:px-6">
          Loading recommendations…
        </p>
      ) : recommendations.length === 0 ? (
        <p className="px-4 pb-10 pt-4 text-center text-sm text-muted-foreground sm:px-6">
          No recommendations yet. Raise one against the resources you think are
          costing more than they earn.
        </p>
      ) : (
        <div className="space-y-3 px-4 pb-6 sm:px-6">
          {recommendations.map((recommendation) => {
            const nextStates =
              RECOMMENDATION_TRANSITIONS[recommendation.state] ?? [];
            return (
              <Card key={recommendation.id}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{recommendation.title}</span>
                        <Badge
                          variant={
                            STATE_VARIANT[recommendation.state] as any
                          }
                        >
                          {RECOMMENDATION_STATE_LABELS[recommendation.state]}
                        </Badge>
                      </div>
                      {recommendation.description && (
                        <p className="text-sm text-muted-foreground">
                          {recommendation.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-start gap-1">
                      <div className="text-right text-sm">
                        {recommendation.estimatedMonthlySaving != null && (
                          <div className="font-medium">
                            {formatCost(
                              Number(recommendation.estimatedMonthlySaving),
                              recommendation.currency,
                            )}
                            /mo
                          </div>
                        )}
                        {recommendation.dueDate && (
                          <div className="text-xs text-muted-foreground">
                            due{' '}
                            {new Date(recommendation.dueDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      {canManage && (
                        <ActionDropdown
                          actions={
                            [
                              {
                                label: 'Delete',
                                icon: <Trash2 className="h-4 w-4" />,
                                onClick: () => setPendingDelete(recommendation),
                                variant: 'danger',
                              },
                            ] as ActionItem[]
                          }
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span>
                      Owner:{' '}
                      {recommendation.assignedTo
                        ? `${recommendation.assignedTo.firstName} ${recommendation.assignedTo.lastName}`
                        : 'unassigned'}
                    </span>
                    <span>
                      {formatCount(recommendation.resources?.length ?? 0)}{' '}
                      resource(s)
                    </span>
                  </div>

                  {recommendation.dismissalReason && (
                    <div className="rounded-md bg-muted/50 p-2 text-sm">
                      <span className="font-medium">Dismissed:</span>{' '}
                      {recommendation.dismissalReason}
                    </div>
                  )}

                  {canManage && (
                    <div className="flex flex-wrap gap-2">
                      {nextStates.map((state) => (
                        <Button
                          key={state}
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleTransition(recommendation, state)
                          }
                        >
                          {state === 'dismissed'
                            ? 'Dismiss'
                            : `Move to ${RECOMMENDATION_STATE_LABELS[state]}`}
                        </Button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(next) => !next && setDialogOpen(false)}>
        <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl sm:p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>Raise Recommendation</DialogTitle>
            <DialogDescription>
              Nothing detects these automatically. The value is the follow-through
              — an owner, a due date, and a decision either way.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <FormField label="Title" required error={errors.title} id="rec-title">
              <Input
                id="rec-title"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Shut down idle staging cluster"
              />
            </FormField>
            <FormField label="Description" id="rec-description">
              <Textarea
                id="rec-description"
                rows={3}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </FormField>
            <div className="grid gap-6 sm:grid-cols-2">
              <FormField label="Category" id="rec-category">
                <Input
                  id="rec-category"
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                  placeholder="Idle resource"
                />
              </FormField>
              {/* One control, as on the resource form — an amount and the
                  currency it is expressed in are a single fact. */}
              <FormField
                label="Estimated Monthly Saving"
                hint="Your estimate — nothing computes this"
                id="rec-saving"
              >
                <div className="flex gap-2">
                  <select
                    className="flex h-10 w-24 shrink-0 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.currency}
                    onChange={(e) => set('currency', e.target.value)}
                    aria-label="Currency"
                  >
                    {currencies.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                  <Input
                    id="rec-saving"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.estimatedMonthlySaving}
                    onChange={(e) => set('estimatedMonthlySaving', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </FormField>
              <FormField label="Owner" id="rec-owner">
                <UserSelect
                  id="rec-owner"
                  users={users}
                  value={form.assignedToId}
                  onChange={(userId) => set('assignedToId', userId ?? '')}
                  placeholder="Unassigned"
                  clearable
                />
              </FormField>
              <FormField label="Review / Due Date" id="rec-due">
                <Input
                  id="rec-due"
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => set('dueDate', e.target.value)}
                />
              </FormField>
            </div>

            <FormField label="Affected Resources" id="rec-resources">
              <div
                id="rec-resources"
                className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2"
              >
                {resources.length === 0 ? (
                  <p className="p-2 text-sm text-muted-foreground">
                    No resources available.
                  </p>
                ) : (
                  resources.map((resource) => (
                    <label
                      key={resource.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-input"
                        checked={form.resourceIds.includes(resource.id)}
                        onChange={() => toggleResource(resource.id)}
                      />
                      {resource.name}
                      <span className="text-xs text-muted-foreground">
                        {resource.providerResourceId}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </FormField>
            </div>

            <DialogFooter className="mx-0 mt-0 shrink-0 gap-2 border-t bg-background px-6 py-3 sm:gap-0 sm:border-t sm:bg-background sm:px-6 sm:py-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="min-w-[140px]">
                {saving ? 'Saving…' : 'Raise Recommendation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(dismissing)} onOpenChange={(next) => !next && setDismissing(null)}>
        <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-lg sm:p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>Dismiss Recommendation</DialogTitle>
            <DialogDescription>
              Dismissing is final. The reason is kept on the record so the
              decision can be revisited later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <FormField label="Reason" required id="dismiss-reason">
              <Textarea
                id="dismiss-reason"
                rows={3}
                value={dismissalReason}
                onChange={(e) => setDismissalReason(e.target.value)}
                placeholder="Why is this not worth doing?"
              />
            </FormField>
          </div>
          <DialogFooter className="mx-0 mt-0 shrink-0 gap-2 border-t bg-background px-6 py-3 sm:gap-0 sm:border-t sm:bg-background sm:px-6 sm:py-3">
            <Button variant="ghost" onClick={() => setDismissing(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDismissal}
              disabled={!dismissalReason.trim()}
              className="min-w-[140px]"
            >
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        show={Boolean(pendingDelete)}
        title="Delete Recommendation"
        message={`Delete "${pendingDelete?.title ?? ''}"? The record and its decision history are removed permanently.`}
        confirmText="Delete"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default CloudRecommendationsTab;
